<app-reusable-modal
  [isOpen]="isOpen"
  [panelClass]="'max-w-4xl'"
  [contentScoped]="true"
  [title]="'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_TITLE' | translate"
  (closed)="onCancel()"
>
  <form
    class="create-invoice-modal"
    [formGroup]="form"
    (ngSubmit)="submitForm()"
    novalidate
  >
    <section class="create-invoice-modal__panel create-invoice-modal__panel--context">
      <div class="create-invoice-modal__context">
        <div class="create-invoice-modal__context-item">
          <span class="form-label">
            {{ 'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_CUSTOMER_LABEL' | translate }}
          </span>
          <p class="create-invoice-modal__readonly">{{ customerLabel || '-' }}</p>
        </div>
        <div class="create-invoice-modal__context-item">
          <span class="form-label">
            {{ 'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_ORDER_LABEL' | translate }}
          </span>
          <p class="create-invoice-modal__readonly">{{ salesOrderNumberLabel || '-' }}</p>
        </div>
      </div>
    </section>

    <section class="create-invoice-modal__panel">
      <div class="create-invoice-modal__field create-invoice-modal__field--date">
        <label class="form-label" for="create-invoice-date">
          {{ 'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_DATE_LABEL' | translate }}
        </label>
        <input
          id="create-invoice-date"
          type="date"
          class="form-input"
          formControlName="invoiceDate"
          [class.is-invalid]="form.controls.invoiceDate.touched && form.controls.invoiceDate.invalid"
        />
        @if (form.controls.invoiceDate.touched && form.controls.invoiceDate.hasError('required')) {
          <ul class="invalid-feedback create-invoice-modal__field-errors">
            <li>{{ 'ERRORS.REQUIRED' | translate }}</li>
          </ul>
        }
      </div>

      <div class="create-invoice-modal__field create-invoice-modal__field--date">
        <label class="form-label" for="create-invoice-due-date">
          {{ 'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_DUE_DATE_LABEL' | translate }}
        </label>
        <input
          id="create-invoice-due-date"
          type="date"
          class="form-input"
          formControlName="dueDate"
        />
      </div>
    </section>

    <section class="create-invoice-modal__panel create-invoice-modal__panel--lines">
      @if (lineRows.length === 0) {
        <p class="create-invoice-modal__empty">
          {{ 'COMPONENTS.SALES_ORDERS.INVOICES.EMPTY_STATE' | translate }}
        </p>
      } @else {
        <div class="create-invoice-modal__lines" formArrayName="lines">
          @for (row of lineRows; track row.index) {
            <article class="create-invoice-modal__line" [formGroupName]="row.index">
              <header class="create-invoice-modal__line-header">
                <span class="create-invoice-modal__line-title">
                  {{ 'COMPONENTS.SALES_ORDERS.LINES.LINE_NUMBER' | translate }} {{ row.index + 1 }}
                </span>
                <span class="create-invoice-modal__line-meta">
                  {{ 'COMPONENTS.SALES_ORDERS.LINES.REMAINING_BADGE' | translate: { remaining: row.remaining } }}
                </span>
              </header>

              <p class="create-invoice-modal__line-description">
                {{ row.description || '-' }}
              </p>
              @if (row.description2) {
                <p class="create-invoice-modal__line-description-secondary">
                  {{ row.description2 }}
                </p>
              }

              <div class="create-invoice-modal__line-grid">
                <div class="create-invoice-modal__field">
                  <label class="form-label" [attr.for]="'create-invoice-line-qty-' + row.index">
                    {{ 'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_LINE_QTY_LABEL' | translate }}
                  </label>
                  <input
                    [id]="'create-invoice-line-qty-' + row.index"
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    class="w-full px-3 py-2 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    formControlName="quantity"
                    [class.is-invalid]="lineForms.at(row.index).controls.quantity.touched && lineForms.at(row.index).controls.quantity.invalid"
                  />
                  @if (lineForms.at(row.index).controls.quantity.touched && lineForms.at(row.index).controls.quantity.invalid) {
                    <ul class="invalid-feedback create-invoice-modal__field-errors">
                      @if (lineForms.at(row.index).controls.quantity.hasError('required')) {
                        <li>{{ 'COMPONENTS.SALES_ORDERS.ERRORS.QUANTITY_REQUIRED' | translate }}</li>
                      }
                      @if (lineForms.at(row.index).controls.quantity.hasError('min')) {
                        <li>{{ 'COMPONENTS.SALES_ORDERS.ERRORS.NEGATIVE_QUANTITY' | translate }}</li>
                      }
                      @if (lineForms.at(row.index).controls.quantity.hasError('max')) {
                        <li>
                          {{ 'COMPONENTS.SALES_ORDERS.ERRORS.OVER_INVOICE' | translate }}
                          ({{ 'COMPONENTS.SALES_ORDERS.LINES.REMAINING_BADGE' | translate: { remaining: row.remaining } }})
                        </li>
                      }
                    </ul>
                  }
                </div>

                <div class="create-invoice-modal__field">
                  <span class="form-label">
                    {{ 'COMPONENTS.SALES_ORDERS.FIELDS.UNIT_OF_MEASURE' | translate }}
                  </span>
                  <p class="create-invoice-modal__readonly">{{ row.unitLabel || '-' }}</p>
                </div>
              </div>
            </article>
          }
        </div>
      }

      @if (errorMessageKey) {
        <p class="create-invoice-modal__error" role="alert">
          {{ errorMessageKey | translate }}
        </p>
      }
    </section>

    <div class="create-invoice-modal__actions">
      <button type="button" class="form-secondary-btn" [disabled]="isSubmitting" (click)="onCancel()">
        {{ 'BUTTONS.CANCEL' | translate }}
      </button>

      <button
        type="submit"
        class="form-primary-btn"
        [disabled]="isSubmitting || form.invalid || lineRows.length === 0"
      >
        @if (isSubmitting) {
          {{ 'COMPONENTS.SALES_ORDERS.MODAL.INVOICE_SUBMITTING' | translate }}
        } @else {
          {{ 'BUTTONS.CREATE' | translate }}
        }
      </button>
    </div>
  </form>
</app-reusable-modal>
